import { SITE_BASE_URL } from '../../../constants';
import { dev } from '@breath-lab/domain';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@breath-lab/state';
import { useJourneys, JourneyWithState } from '@breath-lab/state';
import { useAccessGate } from '../../hooks/useAccessGate';
import { refreshCustomerInfo, getIsPremium } from '@breath-lab/state';
import { breathworkConfigurePath, ROUTES, slugToId, idToSlug } from '../../../routes';
import { fetchSessionToken, type SessionGrant } from '../../../domain/sessionTokenCache';
import { PageMeta } from '../../seo/PageMeta';
import { JsonLd } from '../../seo/JsonLd';
import { getJourneySkeletonMeta } from '../../../seo/routes';
import { Journey } from '../../../domain/models';
import { UserJourney } from '../../../domain/models';
import { JourneyStep } from '../../../domain/models';
import { JourneyStepTechnique } from '../../../domain/models';
import { getServices } from '@breath-lab/services';
import { forkJourneyTemplate, isStepCompleted as isStepCompletedFn } from '../../../domain/logic';
import { JOURNEY_COVER_IMAGES } from '../../../domain/journeyHelpers';
import Button from '@ui/components/Button/Button';
import IconButton from '@ui/components/IconButton/IconButton';
import ConfirmationDialog from '@ui/components/ConfirmationDialog/ConfirmationDialog';
import Modal from '@ui/components/Modal/Modal';
import UpgradePanel from '../../components/UpgradePanel/UpgradePanel';
import InfoTag from '@ui/components/InfoTag/InfoTag';
import AccessBadge from '@ui/components/AccessBadge/AccessBadge';
import { logEvent } from '../../../infra/firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from '../../../domain/analytics';
import { useShare, captureReferralParam } from '../../../domain/sharing';
import { ShareIcon } from '../../components/ShareIcon';
import styles from './JourneyDetailPage.module.css';

export interface CompletedTechnique {
  breathworkId: string;
  durationMs: number;
  rounds: number;
}

export interface JourneyStepRouteState {
  journeyId: string;
  stepId: string;
  stepIndex: number;
  totalSteps: number;
  userJourneyDocId: string;
  // Multi-technique
  techniqueIndex: number;
  techniques: JourneyStepTechnique[];
  stepSessionId: string;
  // Stats from techniques already finished within this step (carried forward
  // so the final success page can show all techniques' results)
  completedTechniques?: CompletedTechnique[];
  // Convenience fields for the configure page (match techniques[techniqueIndex].configOverride)
  rounds?: number;
  breathDuration?: {
    inhaleMs: number;
    fullLungsRetentionMs: number;
    exhaleMs: number;
    emptyLungsRetentionMs: number;
  } | null;
  breathsOnFirstRound?: number | null;
  incrementBetweenRounds?: boolean | null;
  journeyName?: string | null;
  stepTitle?: string | null;
}

const truncateDescription = (s: string, max = 152) =>
  s.length <= max ? s : s.slice(0, s.lastIndexOf(' ', max)) + '…'


function journeyCoverImage(id: string): string | undefined {
  return JOURNEY_COVER_IMAGES[id];
}

function progressPercent(uj: UserJourney, journey: Journey): number {
  return Math.round((uj.completedSteps.length / journey.totalSteps) * 100);
}

function buildStepRouteState(
  journey: Journey,
  userJourneyDocId: string,
  stepIndex: number,
  step: JourneyStep,
  stepSessionId: string,
): JourneyStepRouteState {
  const technique = step.techniques[0];
  return {
    journeyId: journey.id,
    stepId: step.id,
    stepIndex,
    totalSteps: journey.totalSteps,
    userJourneyDocId,
    techniqueIndex: 0,
    techniques: step.techniques,
    stepSessionId,
    rounds: technique.configOverride?.rounds,
    breathDuration: technique.configOverride?.breathDuration ?? null,
    breathsOnFirstRound: technique.configOverride?.breathsOnFirstRound ?? null,
    incrementBetweenRounds: technique.configOverride?.incrementBetweenRounds ?? null,
    journeyName: journey.name.en,
    stepTitle: step.title.en,
  };
}

export default function JourneyDetailPage() {
  const { t } = useTranslation('journeys');
  const { t: tCommon } = useTranslation('common');
  const { id: slug } = useParams<{ id: string }>();
  const id = slugToId(slug ?? '');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, openLoginModal } = useAuth();
  const { journeys, isLoading, error, loadTemplates, reload } = useJourneys();
  const { ctx, isJourneyAccessible } = useAccessGate();
  const uid = user?.uid ?? null;
  useEffect(() => {
    if (isAuthenticated && uid) {
      reload(uid);
    } else {
      loadTemplates();
    }
  }, [uid, isAuthenticated, reload, loadTemplates]);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const [pendingAbandon, setPendingAbandon] = useState<JourneyWithState | null>(null);
  const [showDebugModal, setShowDebugModal] = useState(false);

  const item = journeys.find((j) => j.journey.id === id);

  useEffect(() => {
    if (!isLoading && item) {
      logEvent(AnalyticsEvent.screenView, {
        [AnalyticsParam.screenName]: 'journey_detail',
        [AnalyticsParam.journeyId]: id,
      });
      logEvent(AnalyticsEvent.viewJourney, { [AnalyticsParam.journeyId]: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, item?.journey.id]);

  const searchParams = new URLSearchParams(location.search);
  const refParam = searchParams.get('ref');
  useEffect(() => {
    if (!refParam) return;
    captureReferralParam(refParam, user?.uid ?? null);
    if (item?.journey.id) {
      logEvent(AnalyticsEvent.referralLinkVisit, {
        [AnalyticsParam.referrerUid]: refParam,
        [AnalyticsParam.contentType]: 'journey',
        [AnalyticsParam.id]: item.journey.id,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refParam]);

  const handleShare = useShare(
    'detail',
    'journey',
    item ? idToSlug(item.journey.id) : '',
    item?.journey.name.en ?? '',
    user?.uid ?? null,
  );

  // Show provisional skeleton while loading OR before data has been fetched at all
  // (isLoading starts false; journeys.length === 0 means loadTemplates hasn't returned yet)
  if (journeys.length === 0 && !error) {
    const { name: displayName, description: displayDesc, longDescription } = getJourneySkeletonMeta(id)
    const coverImg = journeyCoverImage(id)
    return (
      <div className={styles.page}>
        <PageMeta
          title={displayName}
          description={displayDesc}
          path={`/journeys/${slug}`}
          image={`${SITE_BASE_URL}/og-journey-${slug}.png`}
          ogType="article"
        />
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'Course',
            name: displayName,
            description: displayDesc,
            provider: { '@type': 'Organization', name: 'Breath Lab' },
            url: `${SITE_BASE_URL}/journeys/${slug}`,
            ...(coverImg ? { image: `${SITE_BASE_URL}${coverImg}` } : {}),
          }}
        />
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_BASE_URL + '/' },
              { '@type': 'ListItem', position: 2, name: 'Journeys', item: SITE_BASE_URL + '/journeys' },
              { '@type': 'ListItem', position: 3, name: displayName },
            ],
          }}
        />
        <div className={styles.skeletonTextPreview}>
          <h1 className={styles.title}>{displayName}</h1>
          <p className={styles.skeletonDesc}>{displayDesc}</p>
          {longDescription && <p className={styles.skeletonLongDesc}>{longDescription}</p>}
        </div>
        <div className={styles.skeletonHeader} />
        <div className={styles.skeletonBody} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <p className={styles.errorText}>{t('errors.couldNotLoadDetail')}</p>
      </div>
    );
  }

  // Only redirect when journeys have loaded and this ID genuinely doesn't exist
  if (!item) {
    return <Navigate to={ROUTES.journeys} replace />;
  }

  const { journey, userJourney } = item;

  const steps = [...journey.steps].sort((a, b) => a.position - b.position);
  const isPremiumJourney = ctx.paywallEnabled && !ctx.freeJourneyIds.includes(journey.id);
  const currentStep = userJourney ? steps[userJourney.currentStepIndex] : null;

  async function handleStart() {
    if (!user) return;
    const step0 = steps[0];
    if (!isJourneyAccessible(journey.id)) {
      setUpgradeLoading(true);
      await refreshCustomerInfo();
      setUpgradeLoading(false);
      if (!getIsPremium()) {
        setUpgradeOpen(true);
        return;
      }
    }
    if (!step0) return;

    const active = useJourneys.getState().activeJourney();
    if (active && active.journey.id !== journey.id) {
      setPendingAbandon(active);
      setShowAbandonModal(true);
      return;
    }

    await proceedWithStart();
  }

  async function proceedWithStart() {
    if (!user) return;
    const step0 = steps[0];
    if (!step0) return;
    setStarting(true);
    try {
      let journeySessionToken: SessionGrant | null = null;
      if (isPremiumJourney) {
        try {
          journeySessionToken = await fetchSessionToken({ journeyId: journey.id });
        } catch {
          setUpgradeOpen(true);
          return;
        }
      }
      let docId: string;
      if (userJourney?.id && (isAbandoned || isCompleted)) {
        await getServices().userData.restartUserJourney(user.uid, userJourney.id);
        docId = userJourney.id;
      } else {
        const newUj = forkJourneyTemplate(journey, user.uid);
        docId = await getServices().userData.saveUserJourney(user.uid, newUj);
      }
      if (uid) reload(uid);
      const stepSessionId = crypto.randomUUID();
      logEvent(AnalyticsEvent.journeyStarted, { [AnalyticsParam.journeyId]: journey.id });
      navigate(breathworkConfigurePath(step0.techniques[0].breathworkId), {
        state: { journeyStep: buildStepRouteState(journey, docId, 0, step0, stepSessionId), journeySessionToken },
      });
    } catch (e) {
      dev.error('[JourneyDetailPage] handleStart failed:', e);
      setStartError(true);
    } finally {
      setStarting(false);
    }
  }

  async function handleAbandonConfirm() {
    if (!user || !pendingAbandon?.userJourney?.id) return;
    logEvent(AnalyticsEvent.journeyAbandoned, { [AnalyticsParam.journeyId]: pendingAbandon.journey.id });
    const docIdToAbandon = pendingAbandon.userJourney.id;
    setShowAbandonModal(false);
    setPendingAbandon(null);
    try {
      await getServices().userData.abandonUserJourney(user.uid, docIdToAbandon);
      await proceedWithStart();
    } catch (e) {
      dev.error('[JourneyDetailPage] abandon failed:', e);
      setStartError(true);
    }
  }

  async function handleDevJumpToStep(stepIndex: number) {
    const step = steps[stepIndex];
    if (!step) return;
    const docId = userJourney?.id ?? 'dev-test-placeholder';
    setShowDebugModal(false);
    let journeySessionToken: SessionGrant | null = null;
    if (isPremiumJourney) {
      try {
        journeySessionToken = await fetchSessionToken({ journeyId: journey.id });
      } catch {
        // dev tool: proceed without token; player will redirect if access is denied
      }
    }
    navigate(breathworkConfigurePath(step.techniques[0].breathworkId), {
      state: { journeyStep: buildStepRouteState(journey, docId, stepIndex, step, crypto.randomUUID()), journeySessionToken },
    });
  }

  async function handleContinue() {
    if (!currentStep || !userJourney?.id) return;
    if (!isJourneyAccessible(journey.id)) {
      setUpgradeLoading(true);
      await refreshCustomerInfo();
      setUpgradeLoading(false);
      if (!getIsPremium()) {
        setUpgradeOpen(true);
        return;
      }
    }
    let journeySessionToken: SessionGrant | null = null;
    if (isPremiumJourney) {
      try {
        journeySessionToken = await fetchSessionToken({ journeyId: journey.id });
      } catch {
        setUpgradeOpen(true);
        return;
      }
    }
    const stepSessionId = crypto.randomUUID();
    navigate(breathworkConfigurePath(currentStep.techniques[0].breathworkId), {
      state: { journeyStep: buildStepRouteState(journey, userJourney.id, userJourney.currentStepIndex, currentStep, stepSessionId), journeySessionToken },
    });
  }

  const pct = userJourney ? progressPercent(userJourney, journey) : 0;
  const isCompleted = userJourney?.status === 'completed';
  const isAbandoned = userJourney?.status === 'abandoned';
  const isActive = userJourney != null && !isCompleted && !isAbandoned;

  return (
    <div className={styles.page}>
      <PageMeta
        title={journey.name.en}
        description={truncateDescription(journey.description.en)}
        path={`/journeys/${idToSlug(journey.id)}`}
        image={`${SITE_BASE_URL}/og-journey-${idToSlug(journey.id)}.png`}
        ogType="article"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: journey.name.en,
          description: journey.description.en,
          provider: { '@type': 'Organization', name: 'Breath Lab' },
          hasCourseInstance: journey.steps.map((_step, i) => ({
            '@type': 'CourseInstance',
            name: `Day ${i + 1}`,
            courseMode: 'online',
          })),
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_BASE_URL + '/' },
            { '@type': 'ListItem', position: 2, name: 'Journeys', item: SITE_BASE_URL + '/journeys' },
            { '@type': 'ListItem', position: 3, name: journey.name.en },
          ],
        }}
      />
      {journeyCoverImage(journey.id) && (
        <img
          src={journeyCoverImage(journey.id)}
          alt={`${journey.name.en} breathwork journey`}
          className={styles.coverImage}
        />
      )}

      {/* Header badges */}
      <div className={styles.badges}>
        <InfoTag>{journey.difficulty.toLowerCase()}</InfoTag>
        <InfoTag>{t('stepCount', { count: journey.totalSteps })}</InfoTag>
        {isPremiumJourney && <AccessBadge variant="premium">{t('premiumBadge')}</AccessBadge>}
      </div>

      {/* Title + theme + goal */}
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{journey.name.en}</h1>
        <IconButton variant="bordered" label={tCommon('share.ariaLabel')} onClick={handleShare}>
          <ShareIcon />
        </IconButton>
      </div>
      <p className={styles.theme}>&ldquo;{journey.description.en}&rdquo;</p>
      <p className={styles.goal}>{journey.goal.en}</p>

      {/* Active state: progress + Continue button */}
      {isActive && userJourney && (
        <>
          <div className={styles.progressMeta}>
            <span>{t('stepOf', { current: userJourney.currentStepIndex + 1, total: journey.totalSteps })}</span>
            <span>{t('percentComplete', { pct })}</span>
          </div>
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${pct}%` }} />
          </div>
          <Button variant="primary" className={styles.primaryAction} onClick={handleContinue} disabled={upgradeLoading}>
            {upgradeLoading ? t('starting') : t('continueJourney')}
          </Button>
        </>
      )}

      {/* Not started or abandoned: Start button */}
      {(!userJourney || isAbandoned) && (
        <>
          {isAuthenticated ? (
            <Button
              variant="primary"
              className={styles.primaryAction}
              onClick={handleStart}
              disabled={starting || upgradeLoading}
            >
              {starting || upgradeLoading ? t('starting') : t('startJourney')}
            </Button>
          ) : (
            <Button
              variant="primary"
              className={styles.primaryAction}
              onClick={() => openLoginModal(location.pathname)}
            >
              {t('signInToStart')}
            </Button>
          )}
          {startError && (
            <p className={styles.errorText}>{t('errors.couldNotStart')}</p>
          )}
        </>
      )}

      {/* Completed state */}
      {isCompleted && (
        <>
          <div className={styles.completedBanner}>
            {t('completed', { name: journey.name.en })}
          </div>
          {isAuthenticated && (
            <Button variant="secondary" className={styles.primaryAction} onClick={handleStart} disabled={starting || upgradeLoading}>
              {starting || upgradeLoading ? t('starting') : t('startAgain')}
            </Button>
          )}
          {startError && (
            <p className={styles.errorText}>{t('errors.couldNotStart')}</p>
          )}
        </>
      )}

      {/* Steps list */}
      <p className={styles.stepsLabel}>
        {userJourney ? t('stepsLabel') : t('whatYoullPractice')}
      </p>

      <ul className={styles.stepsList}>
        {steps.map((step, index) => {
          const isStepCompleted = userJourney
            ? isStepCompletedFn(userJourney, step.id)
            : false;
          const isStepCurrent = userJourney
            ? index === userJourney.currentStepIndex && !isCompleted
            : false;

          return (
            <li
              key={step.id}
              className={[
                styles.stepRow,
                isStepCompleted ? styles.stepCompleted : '',
                isStepCurrent ? styles.stepCurrent : '',
              ].join(' ')}
            >
              <div
                className={[
                  styles.stepCircle,
                  isStepCompleted ? styles.stepCircleDone : '',
                  isStepCurrent ? styles.stepCircleActive : '',
                ].join(' ')}
              >
                {isStepCompleted ? (
                    <svg width="11" height="9" viewBox="0 0 11 9" fill="none" aria-hidden="true">
                      <path d="M1 4.5L4 7.5L10 1" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : index + 1}
              </div>
              <div className={styles.stepInfo}>
                <span className={styles.stepName}>{step.title.en}</span>
                <span className={styles.stepDesc}>{step.description.en}</span>
              </div>
              {isStepCurrent && (
                <Button variant="primary" size="md" className={styles.doThisAction} onClick={handleContinue}>
                  {t('doThisAction')}
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      {import.meta.env.DEV && isAuthenticated && (
        <button className={styles.devTestButton} onClick={() => setShowDebugModal(true)}>
          DEV: Play step…
        </button>
      )}

      {import.meta.env.DEV && showDebugModal && (
        <div className={styles.devModalOverlay} onClick={() => setShowDebugModal(false)}>
          <div className={styles.devModal} onClick={(e) => e.stopPropagation()}>
            <p className={styles.devModalTitle}>Jump to step</p>
            <ul className={styles.devModalList}>
              {steps.map((step, index) => (
                <li key={step.id}>
                  <button
                    className={styles.devModalItem}
                    onClick={() => handleDevJumpToStep(index)}
                  >
                    <span className={styles.devModalItemNum}>{index + 1}</span>
                    <span className={styles.devModalItemName}>{step.title.en}</span>
                    {step.techniques.length > 1 && (
                      <span className={styles.devModalItemBadge}>{step.techniques.length} techniques</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {upgradeOpen && (
        <Modal onClose={() => setUpgradeOpen(false)} wide>
          <UpgradePanel onComplete={() => setUpgradeOpen(false)} />
        </Modal>
      )}

      {showAbandonModal && pendingAbandon && (
        <ConfirmationDialog
          title={t('abandonModal.title')}
          message={t('abandonModal.body', {
            newJourney: journey.name.en,
            activeJourney: pendingAbandon.journey.name.en,
          })}
          confirmLabel={t('abandonModal.confirm', { newJourney: journey.name.en })}
          onConfirm={handleAbandonConfirm}
          onCancel={() => { setShowAbandonModal(false); setPendingAbandon(null); }}
        />
      )}
    </div>
  );
}
